import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Checkbox,
  Autocomplete,
  TextField as MuiTextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const EditModalOpen = ({
  editModalOpen,
  closeEditModal,
  formData,
  handleInputChange,
  handleFileChange,
  handleSubmit,
  error,
  productList,
  variantNameMap,
}) => {
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Effect to set selectedEvent based on formData.productId when component mounts or formData changes
  useEffect(() => {
    if (formData.productId) {
      const selected = productList.find(
        (product) => String(product.product_id) === String(formData.productId)
      );
      setSelectedEvent(selected || null);
    } else {
      setSelectedEvent(null);
    }
  }, [formData.productId, productList]);

  // Don't render if the modal is not open
  if (!editModalOpen) return null;

  // Handles selection of the main event (product_id)
  const handleEventSelection = (event, newValue) => {
    if (newValue) {
      setSelectedEvent(newValue);
      handleInputChange({
        target: { name: 'productId', value: newValue.product_id || '' },
      });
      // Clear productVariantId when a new main event is selected
      handleInputChange({
        target: { name: 'productVariantId', value: '' },
      });
    } else {
      setSelectedEvent(null);
      handleInputChange({
        target: { name: 'productId', value: '' },
      });
      handleInputChange({
        target: { name: 'productVariantId', value: '' },
      });
    }
  };

  // Handles selection of a specific product variant
  const handleVariantSelection = (option, variant) => {
    // Set both the parent product ID and the specific variant ID
    handleInputChange({
      target: { name: 'productId', value: option.product_id || '' },
    });
    handleInputChange({
      target: { name: 'productVariantId', value: variant.variation_id || '' },
    });
    // Update the local state for the selected event to reflect this choice
    setSelectedEvent(option);
  };

  return ReactDOM.createPortal(
    <Dialog
      open={editModalOpen}
      onClose={closeEditModal}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'visible',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0,0,0,0.5)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: (theme) => theme.palette.primary.main,
          color: 'white',
          py: 1.5,
        }}
      >
        <Typography variant="h6">Edit Exhibitor List</Typography>
        <IconButton edge="end" color="inherit" onClick={closeEditModal} sx={{ p: 0.5 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Autocomplete
            options={productList}
            getOptionLabel={(option) => option.event_name || ''}
            value={selectedEvent}
            onChange={handleEventSelection} // Use the new event selection handler
            disableCloseOnSelect // Keep this to allow variant selection before closing
            renderOption={(props, option) => {
              const hasVariants = option.product_variants?.length > 0;
              // Determine if the main event checkbox should be checked
              // It's checked if its product_id matches formData.productId
              // AND either no variant is selected OR the selected variant belongs to a DIFFERENT product
              const isMainEventChecked =
                String(formData.productId) === String(option.product_id) &&
                !formData.productVariantId; // Only checked if no variant is selected for this product

              return (
                <li {...props}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* Main Event Row */}
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent the main li click from affecting
                        // When the main event row is clicked, select this event and clear any variant
                        handleEventSelection(e, option);
                      }}
                    >
                      <Checkbox
                        checked={isMainEventChecked}
                        tabIndex={-1}
                        disableRipple
                        // Clicking the checkbox directly should also trigger the same logic
                        onChange={(e) => {
                          e.stopPropagation();
                          handleEventSelection(e, option);
                        }}
                      />
                      <Typography variant="body1">{option.event_name}</Typography>
                    </Box>

                    {/* Product Variants (if available) */}
                    {hasVariants && (
                      <Box sx={{ pl: 4 }}>
                        {option.product_variants.map((variant) => {
                          // Determine if this specific variant checkbox should be checked
                          const isVariantChecked =
                            String(formData.productVariantId) === String(variant.variation_id) &&
                            String(formData.productId) === String(option.product_id);

                          return (
                            <Box
                              key={variant.variation_id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: isVariantChecked ? '#f0f0f0' : 'transparent',
                                borderRadius: 1,
                                px: 1,
                                py: 0.5,
                                mt: 0.5,
                                cursor: 'pointer',
                              }}
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent Autocomplete from closing immediately
                                handleVariantSelection(option, variant);
                              }}
                            >
                              <Checkbox
                                checked={isVariantChecked}
                                tabIndex={-1}
                                disableRipple
                                onChange={(e) => {
                                    e.stopPropagation(); // Prevent Autocomplete from closing immediately
                                    handleVariantSelection(option, variant);
                                }}
                              />
                              <Typography variant="body2">{variant.name || 'Unnamed Variant'}</Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                label="Select Event Name"
                placeholder="Search event name"
                fullWidth
                required
              />
            )}
          />

          {/* Display selected variant name if any */}
          {formData.productVariantId && variantNameMap && variantNameMap[formData.productVariantId] && (
            <Typography variant="body2" sx={{ mt: 0.5, mb: 1, color: 'text.secondary' }}>
              - {variantNameMap[formData.productVariantId]}
            </Typography>
          )}

          {/* CSV Upload Button */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<InsertDriveFileIcon />}
              fullWidth
            >
              Upload CSV (optional)
              <input type="file" accept=".csv" onChange={handleFileChange} hidden />
            </Button>
          </Box>

          {/* Error Message */}
          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}

          {/* Dialog Actions (Cancel and Update Buttons) */}
          <DialogActions sx={{ px: 0, pt: 3 }}>
            <Button variant="outlined" onClick={closeEditModal} sx={{ minWidth: 100 }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" sx={{ minWidth: 100 }}>
              Update
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>,
    document.body
  );
};

export default EditModalOpen;